import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useRateLimitedMutation } from "@/lib/use-rate-limited-mutation";
import type { LoginRequest, LoginResponse } from "@/types/auth";
import type {
	ChangePasswordRequest,
	ConfirmRegisterTokenRequest,
	DeleteAccountRequest,
	PasswordResetConfirmRequest,
	PasswordResetRequest,
} from "./types";

const LOGIN_RATE_LIMIT = {
	key: "auth:login",
	limit: 10,
	windowMs: 5 * 60 * 1000,
	persist: false,
} as const;

export function useLogin() {
	return useRateLimitedMutation(
		{
			mutationFn: async (data: LoginRequest): Promise<LoginResponse> => {
				const response = await api.post<LoginResponse>(
					"/api/auth/login/",
					data,
				);
				return response.data;
			},
		},
		LOGIN_RATE_LIMIT,
	);
}

export function useChangePassword() {
	return useMutation({
		mutationFn: async (data: ChangePasswordRequest) => {
			const res = await api.post("/api/auth/change_password/", data);
			return res.data;
		},
	});
}

export function usePasswordReset() {
	return useMutation({
		mutationFn: async (data: PasswordResetRequest) => {
			const res = await api.post("/api/auth/password_reset/", data);
			return res.data;
		},
	});
}

export function usePasswordResetConfirm() {
	return useMutation({
		mutationFn: async (data: PasswordResetConfirmRequest) => {
			const res = await api.post("/api/auth/password_reset/confirm/", data);
			return res.data;
		},
	});
}

export function useConfirmRegisterToken() {
	return useMutation({
		mutationFn: async (data: ConfirmRegisterTokenRequest) => {
			const res = await api.get("/api/auth/confirm_register_token", {
				params: data,
			});
			return res.data;
		},
	});
}

export function useDeleteAccount() {
	return useMutation({
		mutationFn: async (data: DeleteAccountRequest) => {
			const res = await api.post("/api/auth/delete_account/", data);
			return res.data;
		},
	});
}
